/**
 * Enrolled Children List Tab Component
 * Fully customizable print/download for compliance reporting
 * - Column selector (always visible)
 * - Print mode: All / By Category / Select Individual
 * - Summary cards optional on report
 * - "Enrolled" column always excluded
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Users, Printer, Loader2, Calendar, CheckSquare, Square } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLUMNS = [
  { key: "dob",          label: "DOB" },
  { key: "age",          label: "Age" },
  { key: "classroom",    label: "Classroom" },
  { key: "parent",       label: "Parent/Guardian" },
  { key: "phone",        label: "Phone" },
  { key: "enrollDate",   label: "Enrollment Date" },
];

export const EnrolledChildrenListTab = () => {
  const { organization } = useAuth();
  const [loading, setLoading]         = useState(true);
  const [children, setChildren]       = useState([]);

  // Print options — always visible
  const [showSummaryCards, setShowSummaryCards]   = useState(true);
  const [selectedCols, setSelectedCols]           = useState({ dob: true, age: true, classroom: true, parent: true, phone: true, enrollDate: false });
  const [printMode, setPrintMode]                 = useState("all"); // all | category | select
  const [categoryFilter, setCategoryFilter]       = useState("all"); // all | infant | preschool | schoolage
  const [selectedIds, setSelectedIds]             = useState(new Set());

  useEffect(() => { fetchChildren(); }, []);

  const fetchChildren = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/children`);
      const active = (res.data || []).filter(c => c.active !== false);
      setChildren(active);
    } catch {
      toast.error("Failed to load children");
    } finally {
      setLoading(false);
    }
  };

  const getChildName = (child) => child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim() || 'N/A';

  const getChildGroup = (child) => child.fullTimeSubcategory || child.ageGroup || child.classroom || child.category || 'Unassigned';

  const getChildDOB = (child) => child.birthdate || child.dateOfBirth || '';

  const getChildParent = (child) => {
    const p = child.pickupPersons?.[0] || child.parents?.[0] || child.guardian || {};
    return (p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()) || 'N/A';
  };

  const getChildPhone = (child) => {
    const p = child.pickupPersons?.[0] || child.parents?.[0] || child.guardian || {};
    return p.phone || p.cellPhone || 'N/A';
  };

  const getChildStartDate = (child) => child.startDate || child.enrollmentDate || '';

  const calcAge = (dob) => {
    if (!dob) return "N/A";
    const b = new Date(dob), t = new Date();
    const m = (t.getFullYear() - b.getFullYear()) * 12 + (t.getMonth() - b.getMonth());
    if (m < 12) return `${m} mo`;
    const y = Math.floor(m / 12), rm = m % 12;
    return rm > 0 ? `${y}y ${rm}m` : `${y}y`;
  };

  // All unique ageGroups from actual data
  const ageGroups = [...new Set(children.map(c => getChildGroup(c)).filter(Boolean))].sort();

  const getGroupChildren = (group) =>
    group === "all" ? children : children.filter(c => getChildGroup(c) === group);

  const getChildrenToPrint = () => {
    if (printMode === "all")      return children;
    if (printMode === "category") return getGroupChildren(categoryFilter);
    if (printMode === "select")   return children.filter(c => selectedIds.has(c.id || c._id));
    return children;
  };

  const toggleChild = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAllChildren = () => {
    if (selectedIds.size === children.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(children.map(c => c.id || c._id)));
    }
  };

  const toggleCol = (key) => setSelectedCols(p => ({ ...p, [key]: !p[key] }));

  // Build print HTML for a group of children
  const buildTableHTML = (group, startIdx = 1) => {
    const cols = COLUMNS.filter(c => selectedCols[c.key]);
    const headers = ['#', 'Child Name', ...cols.map(c => c.label)].map(h => `<th>${h}</th>`).join('');
    const rows = group.map((child, i) => {
      const cells = cols.map(col => {
        if (col.key === "dob")        return `<td>${getChildDOB(child) ? new Date(getChildDOB(child) + 'T12:00:00').toLocaleDateString() : 'N/A'}</td>`;
        if (col.key === "age")        return `<td>${calcAge(getChildDOB(child))}</td>`;
        if (col.key === "classroom")  return `<td>${getChildGroup(child)}</td>`;
        if (col.key === "parent")     return `<td>${getChildParent(child)}</td>`;
        if (col.key === "phone")      return `<td>${getChildPhone(child)}</td>`;
        if (col.key === "enrollDate") return `<td>${getChildStartDate(child) ? new Date(getChildStartDate(child) + 'T12:00:00').toLocaleDateString() : 'N/A'}</td>`;
        return '<td>N/A</td>';
      }).join('');
      return `<tr class="${i % 2 === 0 ? '' : 'alt'}"><td>${startIdx + i}</td><td class="name">${getChildName(child)}</td>${cells}</tr>`;
    }).join('');
    return `<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
  };

  const handlePrint = () => {
    const centerName = organization?.name || 'Childcare Center';
    const centerAddress = organization?.address || '';
    const licenseNumber = organization?.licenseNumber || '';
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error("Allow popups to print"); return; }

    const summaryHTML = showSummaryCards ? `
      <div class="summary">
        <div class="si"><div class="sv">${children.length}</div><div class="sl">Total Enrolled</div></div>
        ${ageGroups.map(g => `<div class="si"><div class="sv">${getGroupChildren(g).length}</div><div class="sl">${g}</div></div>`).join('')}
      </div>` : '';

    let bodyHTML = '';
    if (printMode === "category" && categoryFilter === "all") {
      // Print each ageGroup as a separate section
      let counter = 1;
      ageGroups.forEach(group => {
        const grp = getGroupChildren(group);
        if (grp.length === 0) return;
        bodyHTML += `<div class="section"><h3 class="cat-title">${group} (${grp.length} children)</h3>${buildTableHTML(grp, counter)}</div>`;
        counter += grp.length;
      });
    } else {
      const group = getChildrenToPrint();
      bodyHTML = buildTableHTML(group);
    }

    const totalPrinted = getChildrenToPrint().length;

    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>Enrolled Children List - ${centerName}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 30px; max-width: 1050px; margin: 0 auto; font-size: 10px; }
        .logo img { height: 45px; }
        .cn { text-align: center; font-size: 20px; font-weight: bold; color: #1F2937; margin: 6px 0; }
        .hdr { text-align: center; border-bottom: 2px solid #1a365d; padding-bottom: 12px; margin-bottom: 12px; }
        .rt { font-size: 14px; color: #1a365d; font-weight: bold; }
        .dt { font-size: 11px; color: #888; margin-top: 4px; }
        .lic { font-size: 11px; color: #666; }
        .summary { display: flex; justify-content: center; gap: 28px; margin: 12px 0; padding: 10px; background: #f0f4f8; border-radius: 5px; }
        .si { text-align: center; }
        .sv { font-size: 18px; font-weight: bold; color: #1a365d; }
        .sl { font-size: 10px; color: #666; }
        .section { margin-bottom: 24px; }
        .cat-title { color: #1a365d; font-size: 13px; font-weight: bold; border-bottom: 1px solid #1a365d; padding-bottom: 4px; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th { background: #1a365d; color: white; padding: 7px 8px; text-align: left; font-size: 10px; }
        td { padding: 5px 8px; border-bottom: 1px solid #ddd; }
        tr.alt td { background: #f5f8ff; }
        .name { font-weight: bold; }
        .footer { margin-top: 16px; text-align: center; font-size: 10px; color: #888; border-top: 1px solid #ddd; padding-top: 8px; }
        @media print { body { padding: 12px; } }
      </style></head><body>
      <div class="logo"><img src="/simple-sign-in-logo.png" onerror="this.style.display='none'" /></div>
      <div class="cn">${centerName}</div>
      <div class="hdr">
        ${licenseNumber ? `<div class="lic">License #: ${licenseNumber}</div>` : ''}
        <div class="rt">ENROLLED CHILDREN LIST</div>
        <div class="dt">Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
      </div>
      ${summaryHTML}
      ${bodyHTML}
      <div class="footer">
        <p>Total Children on This List: ${totalPrinted} | ${centerName}${centerAddress ? ' | ' + centerAddress : ''}</p>
      </div>
    </body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Card><CardContent className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-blue-600" />
            Enrolled Children List
          </CardTitle>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            {new Date().toLocaleDateString()}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* ── Summary Cards ── */}
        <div className="flex flex-wrap gap-2">
          <div className="bg-blue-50 rounded-lg px-4 py-2.5 text-center min-w-[90px]">
            <div className="text-xl font-bold text-blue-700">{children.length}</div>
            <div className="text-xs text-blue-600">Total Enrolled</div>
          </div>
          {ageGroups.map(group => (
            <div key={group} className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-center min-w-[90px]">
              <div className="text-xl font-bold text-gray-700">{getGroupChildren(group).length}</div>
              <div className="text-xs text-gray-500 truncate max-w-[100px]">{group}</div>
            </div>
          ))}
        </div>

        {/* ── Print Controls (always visible) ── */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
          <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Print / Report Options</p>

          {/* Column selector */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Columns to include:</p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-1.5">
                <Checkbox id="col-summary" checked={showSummaryCards} onCheckedChange={v => setShowSummaryCards(!!v)} />
                <Label htmlFor="col-summary" className="text-xs cursor-pointer">Summary Cards</Label>
              </div>
              {COLUMNS.map(col => (
                <div key={col.key} className="flex items-center gap-1.5">
                  <Checkbox id={`col-${col.key}`} checked={!!selectedCols[col.key]} onCheckedChange={() => toggleCol(col.key)} />
                  <Label htmlFor={`col-${col.key}`} className="text-xs cursor-pointer">{col.label}</Label>
                </div>
              ))}
            </div>
          </div>

          {/* Print mode */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Print mode:</p>
            <div className="flex gap-3 flex-wrap">
              {[
                { val: "all",      label: "All Children" },
                { val: "category", label: "By Category" },
                { val: "select",   label: "Select Children" },
              ].map(m => (
                <button
                  type="button"
                  key={m.val}
                  onClick={() => setPrintMode(m.val)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    printMode === m.val
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {/* Category sub-select — dynamic from actual ageGroup values */}
            {printMode === "category" && (
              <div className="mt-2 space-y-1.5">
                <p className="text-xs text-gray-500">
                  Selected: <strong className="text-indigo-600">{categoryFilter === "all" ? "All Groups (separate sections)" : categoryFilter}</strong>
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setCategoryFilter("all")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                      categoryFilter === "all"
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-md'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600'
                    }`}
                  >
                    All Groups — separate sections ({children.length})
                  </button>
                  {ageGroups.filter(g => g !== 'Unassigned').map(group => (
                    <button
                      type="button"
                      key={group}
                      onClick={() => setCategoryFilter(group)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                        categoryFilter === group
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-md'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600'
                      }`}
                    >
                      {group} ({getGroupChildren(group).length})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="print-enrolled-list">
            <Printer className="w-4 h-4 mr-2" />
            Print Report
            {printMode === "select" && selectedIds.size > 0 && ` (${selectedIds.size} selected)`}
          </Button>
        </div>

        {/* ── Preview Table ── */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-blue-600 text-white">
              <tr>
                {printMode === "select" && (
                  <th className="px-2 py-2 w-8">
                    <button onClick={toggleAllChildren} className="text-white">
                      {selectedIds.size === children.length
                        ? <CheckSquare className="w-4 h-4" />
                        : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                )}
                <th className="px-3 py-2 text-left">Child Name</th>
                {selectedCols.age       && <th className="px-3 py-2 text-left">Age</th>}
                {selectedCols.classroom && <th className="px-3 py-2 text-left">Classroom</th>}
                {selectedCols.parent    && <th className="px-3 py-2 text-left">Parent/Guardian</th>}
              </tr>
            </thead>
            <tbody>
              {children.map((child, idx) => {
                const id = child.id || child._id;
                const isSelected = selectedIds.has(id);
                const childGroup = getChildGroup(child);
                const showRow = printMode !== "category" || categoryFilter === "all" || childGroup === categoryFilter;
                if (!showRow) return null;
                return (
                  <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'} ${printMode === "select" && isSelected ? 'ring-1 ring-blue-400' : ''} cursor-pointer`}
                    onClick={() => printMode === "select" && toggleChild(id)}>
                    {printMode === "select" && (<td className="px-2 py-1.5"><Checkbox checked={isSelected} onCheckedChange={() => toggleChild(id)} /></td>)}
                    <td className="px-3 py-1.5 font-medium">{getChildName(child)}</td>
                    {selectedCols.age       && <td className="px-3 py-1.5">{calcAge(getChildDOB(child))}</td>}
                    {selectedCols.classroom && <td className="px-3 py-1.5">{getChildGroup(child)}</td>}
                    {selectedCols.parent    && <td className="px-3 py-1.5">{getChildParent(child)}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {children.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-500">No enrolled children found</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EnrolledChildrenListTab;
